<template>
    <ion-app>
        <WebNavDrawer id="mobile-nav-drawer" v-if="!isNative" />
        <UniversalHeader />
        <IonRouterOutlet id="main-content" class="app page-padding" />
        <TabNavigation id="mobile-nav-tabs" v-if="isNative" />
    </ion-app>
</template>

<script lang="ts">
import { IonApp, IonRouterOutlet, isPlatform } from '@ionic/vue';
import { defineComponent, computed, watch, onMounted, onUnmounted } from 'vue';
import { App } from '@capacitor/app';
import { useStore } from 'vuex';
import useVault from '@/services/vaultService';
import useAuth from '@/services/authService';

import { BrowserVault, Vault } from '@ionic-enterprise/identity-vault';
import UniversalHeader from '@/components/layout/universalHeader.vue';
import TabNavigation from '@/components/layout/tabNavigation.vue';
import WebNavDrawer from './components/layout/webNavDrawer.vue';
import { SplashScreen } from '@capacitor/splash-screen';
import { Preferences } from '@capacitor/preferences';
import { errorToast } from '@/services/pageService';
import { localStorageEnum, setLocalStorage } from '@/services/localStorageService';
import { initializeBraze } from '@/services/braze/brazeSync';
import { initializePushNotifications } from '@/services/pushNotificationService';

export default defineComponent({
    name: 'App',
    components: {
        IonApp,
        IonRouterOutlet,
        UniversalHeader,
        WebNavDrawer,
        TabNavigation,
    },
    setup() {
        const store = useStore();
        const { vault, clearVault, unlockVault, initializeUnlockMode } = useVault() as {
            vault: Vault | BrowserVault;
            clearVault: any;
            unlockVault: any;
            initializeUnlockMode: any;
        };

        // No refreshToken when logged into the browser
        const onResume = (event: any) => {
            if (event && event.isActive) isAuthenticated();
        };

        onMounted(async () => {
            App.addListener('appStateChange', onResume);
            if (!isNative.value && !isPlatform('hybrid')) {
                var donGTM = document.createElement('script');
                donGTM.type = 'text/javascript';
                donGTM.src = '/js/don-gtm.js';

                document.head.appendChild(donGTM);
            }

            // Clear vault when the native build version changes (fresh install or new Appflow build)
            // prevents stale Identity Vault state from blocking the app after a new native binary
            if (isPlatform('hybrid')) {
                try {
                    const { build } = await App.getInfo();
                    const { value: storedBuild } = await Preferences.get({ key: 'app_build_number' });
                    if (storedBuild !== build) {
                        await clearVault();
                        await Preferences.set({ key: 'app_build_number', value: build });
                        // Reload the WebView so the app starts clean with an empty vault
                        // rather than continuing with stale state already partially loaded
                        window.location.reload();
                        return;
                    }
                } catch {
                    // App.getInfo() unavailable on web; skip
                }
            }

            // Initialize vault as soon as app mounts
            initializeUnlockMode();

            // Initialize Braze SDK
            try {
                await initializeBraze();
            } catch (err) {
                // Silently fail - Braze initialization is non-critical
            }
        });

        onUnmounted(() => {
            App.removeAllListeners();
        });

        const { isAuthenticated } = useAuth();
        vault.onLock(async () => {
            const isEmpty = await vault.isEmpty();
            if (!isEmpty) {
                unlockVault().catch(() => {
                    // exampleError: {"extra":[],"code":8,"message":"User canceled auth attempt."}
                    store.dispatch('setLogout', '/').then(() => errorToast("Authentication failed. You've been logged out."));
                    clearVault();
                });
            }
        });
        // }

        store.dispatch('setDevice');
        store.dispatch('setContentBlockList');

        onMounted(() => SplashScreen.hide({ fadeOutDuration: 1000 }));
        //@ts-ignore
        onMounted(() => (window.prerenderReady = true));

        onMounted(async () => {
            try {
                await initializePushNotifications();
            } catch (err) {
                console.error('Push notification init failed:', err);
            }
        });

        const activePageAlert = computed(() => store.getters.activePageAlert);
        watch(activePageAlert, (callback) => callback && callback());

        const isNative = computed(() => store.getters.isNative);

        window.addEventListener('resize', () => store.dispatch('setDevice'));

        const validCoupons = computed(() => store.getters.getValidCoupons);

        watch(
            validCoupons,
            function (newVal) {
                setLocalStorage(localStorageEnum.coupons, newVal);
            },
            { deep: true }
        );

        return { isNative };
    },
});
</script>

<style scoped>
#mobile-nav-tabs {
    position: fixed;
    width: 100%;
    left: 0;
    bottom: 0%;
}
</style>
